import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/AdminRecentSales.css';

const AdminRecentSales = ({ selectedPOS, onReload }) => {
  const [recentSales, setRecentSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [currentSaleId, setCurrentSaleId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelError, setCancelError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    const fetchRecentSales = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('token');
        if (!token) {
          console.error('No se encontró token de autenticación');
          setError('Error de autenticación');
          setLoading(false);
          return;
        }
        
        // Construir URL con el filtro de punto de venta si es necesario
        let url = '/api/admin-stats/recent-sales';
        if (selectedPOS && selectedPOS !== 'all') {
          url += `?pointOfSaleId=${selectedPOS}`;
        }
        
        console.log('AdminRecentSales: Consultando URL:', url);
        
        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        console.log('Datos de ventas recientes recibidos:', response.data);
        
        if (response.data && response.data.data) {
          setRecentSales(response.data.data || []);
        } else {
          setRecentSales([]);
          console.error('Formato de datos inválido:', response.data);
        }
      } catch (err) {
        console.error('Error al cargar ventas recientes:', err);
        // Si es un error 404 o el usuario no tiene negocio registrado, mostrar mensaje apropiado
        if (err.response?.status === 404 || err.response?.status === 500) {
          setRecentSales([]);
          setError(null);
        } else {
          setError('Error al cargar ventas recientes');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRecentSales();
  }, [selectedPOS]);

  const handleCancelSale = (saleId) => {
    setCurrentSaleId(saleId);
    setShowCancelModal(true);
    setCancelError('');
  };

  const handleCloseModal = () => {
    setShowCancelModal(false);
    setCurrentSaleId(null);
    setCancelReason('');
    setCancelError('');
  };

  const handleConfirmCancel = async () => {
    if (!cancelReason) {
      setCancelError('Por favor seleccione una razón de cancelación');
      return;
    }

    try {
      setIsSubmitting(true);
      setCancelError('');

      const response = await fetch('/api/admin-stats/cancel-sale', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          saleId: currentSaleId,
          voidReason: cancelReason
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al cancelar la venta');
      }

      // Cerrar el modal y actualizar la lista de ventas
      handleCloseModal();
      
      // Mostrar mensaje de éxito en el popup
      setSuccessMessage('Venta cancelada correctamente');
      setShowSuccessModal(true);
      
      // Actualizar la lista de ventas (recargar datos)
      if (onReload) {
        onReload();
      }
    } catch (error) {
      console.error('Error al cancelar la venta:', error);
      setCancelError(error.message || 'Error al cancelar la venta');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="recent-sales-container">
        <h2>Ventas Recientes</h2>
        <p>Cargando ventas recientes...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="recent-sales-container">
        <h2>Ventas Recientes</h2>
        <p className="error-message">{error}</p>
      </div>
    );
  }

  return (
    <div className="recent-sales-container">
      <h2>Ventas Recientes</h2>
      {recentSales && recentSales.length > 0 ? (
        <div className="recent-sales-list">
          <table className="admin-recent-sales-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Punto de Venta</th>
                <th>Método de Pago</th>
                <th>Total</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {recentSales.map((sale) => (
                <tr key={sale.id}>
                  <td>
                    {/* Mostrar fecha en formato aaaa-mm-dd */}
                    {sale.sale_fecha ? (() => {
                      try {
                        // Si ya viene en formato correcto (aaaa-mm-dd), usar directamente
                        if (typeof sale.sale_fecha === 'string' && sale.sale_fecha.match(/^\d{4}-\d{2}-\d{2}/)) {
                          return sale.sale_fecha.substring(0, 10);
                        }
                        // Si viene en otro formato, convertir
                        const date = new Date(sale.sale_fecha);
                        if (isNaN(date.getTime())) {
                          return 'N/A';
                        }
                        return date.toISOString().substring(0, 10);
                      } catch (error) {
                        console.error('Error al formatear fecha:', error);
                        return 'N/A';
                      }
                    })() : 'N/A'}
                  </td>
                  <td>{sale.points_of_sale?.pos_name || 'N/A'}</td>
                  <td>{sale.sale_tipopago === 'efectivo' ? 'Efectivo' : 
                       sale.sale_tipopago === 'tarjeta' ? 'Tarjeta' : 
                       sale.sale_tipopago === 'transferencia' ? 'Transferencia' : 
                       sale.sale_tipopago}</td>
                  <td className="total-column">${parseFloat(sale.total || 0).toFixed(2)}</td>
                  <td className="actions-column">
                    <button 
                      className="cancel-button"
                      onClick={() => handleCancelSale(sale.id)}
                    >
                      Cancelar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p>Sin datos de venta para mostrar.</p>
      )}
      
      {/* Modal de confirmación para cancelar venta */}
      {showCancelModal && (
        <div className="modal-overlay">
          <div className="cancel-modal">
            <div className="modal-header">
              <h3>Cancelar Venta</h3>
              <button className="close-button" onClick={handleCloseModal}>&times;</button>
            </div>
            <div className="modal-body">
              <p>¿Está seguro que desea cancelar esta venta? Esta acción no se puede deshacer.</p>
              <p><strong>Importante:</strong> La venta cancelada no puede ser recuperada y deberá ser registrada nuevamente si es necesario.</p>
              
              <div className="form-group">
                <label>Razón de cancelación:</label>
                <select 
                  value={cancelReason} 
                  onChange={(e) => setCancelReason(e.target.value)}
                  className={cancelError ? "error-select" : ""}
                >
                  <option value="">Seleccione una razón</option>
                  <option value="Devolución">Devolución</option>
                  <option value="Error en Monto">Error en Monto</option>
                  <option value="Error del Vendedor">Error del Vendedor</option>
                </select>
                {cancelError && <div className="error-message">{cancelError}</div>}
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="cancel-button" 
                onClick={handleCloseModal} 
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button 
                className="confirm-button" 
                onClick={handleConfirmCancel} 
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Procesando...' : 'Confirmar Cancelación'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de éxito */}
      {showSuccessModal && (
        <div className="modal-overlay">
          <div className="success-modal">
            <div className="modal-header success-header">
              <h3>Operación Exitosa</h3>
              <button className="close-button" onClick={() => setShowSuccessModal(false)}>&times;</button>
            </div>
            <div className="modal-body success-body">
              <div className="success-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </div>
              <p className="success-message">{successMessage}</p>
            </div>
            <div className="modal-footer success-footer">
              <button 
                className="success-button" 
                onClick={() => {
                  setShowSuccessModal(false);
                  // Refrescar la página para actualizar todas las estadísticas
                  window.location.reload();
                }}
              >
                Aceptar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRecentSales;
